import type { ReactNode } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { Box } from '@theme-ui/components';
import pick from 'lodash.pick';
import { css } from '@emotion/react';

import { UnstyledButton } from '@app/components/Button';
import Link from '@app/components/Link';
import useMediaQuery from '@app/helpers/useMediaQuery';
import theme from '@app/styles/theme';
import { screenExtraSmall } from '@app/styles/theme/breakpoints';
import { rem } from '@app/styles/theme/layout';

import FormattedMoney from '../../../components/FormattedMoney';
import type { OrderHistoryOrderFragment } from './__graphql__/OrderHistory';
import { useShowReturnHandler } from './ReturnRequest/hooks/useShowReturnHandler';
import returnRequestMessages from './ReturnRequest/ReturnRequest.messages';
// import { useShowReturnHandler } from './ReturnRequest/useShowReturnHandler';
import {
  OrderHistoryTable,
  OrderHistoryTableMobile,
} from './OrderHistory.styles';
import messages from './OrderHistoryItems.messages';

function ReturnInitiationButton({
  order,
  onClickReturn,
}: {
  order: OrderHistoryOrderFragment;
  onClickReturn: (orderId: string) => void;
}) {
  const { showReturnHandler } = useShowReturnHandler(
    pick(order, ['orderDate', 'statusCode', 'items']),
  );
  const hasReturnStarted = Boolean(
    order.items?.some((item) => item?.returnStatus),
  );

  if (hasReturnStarted) {
    return (
      <span>
        <FormattedMessage {...returnRequestMessages.returnRequestSubmitted} />
      </span>
    );
  }

  if (!showReturnHandler) {
    return null;
  }

  return (
    <UnstyledButton
      onClick={() => {
        onClickReturn(order.id);
      }}
      css={css`
        color: ${theme.colors.lightBlue};
        cursor: pointer;
      `}
    >
      <FormattedMessage {...returnRequestMessages.returnRequestButton} />
    </UnstyledButton>
  );
}

type OrderHistoryItemsProps = {
  items: Array<OrderHistoryOrderFragment | null>;
  onClickReturn: (orderId: string) => void;
};
const OrderHistoryItems = ({
  items,
  onClickReturn,
}: OrderHistoryItemsProps) => {
  return (
    <OrderHistoryTableWrapper>
      <thead>
        <tr>
          <th>
            <FormattedMessage {...messages.orderDate} />
          </th>
          <th data-table="order-products">
            <FormattedMessage {...messages.orderProducts} />
          </th>
          <th>
            <FormattedMessage {...messages.orderReference} />
          </th>
          <th>
            <FormattedMessage {...messages.orderTotal} />
          </th>

          <th>
            <FormattedMessage {...messages.returnRequest} />
          </th>

          <th />
        </tr>
      </thead>

      <tbody>
        {items?.map((order) =>
          order ? (
            <tr key={order.id} data-testid={`order-${order?.number}`}>
              <td data-testid={`order-${order?.number}-date`}>
                <FormattedDate
                  year="2-digit"
                  month="2-digit"
                  day="2-digit"
                  value={new Date(order.orderDate.replace(/-/g, '/'))}
                />
              </td>

              <td data-table="order-products">
                {order.items?.map((orderItem, index) => (
                  <span key={index}>
                    <span
                      data-testid={`order-${order?.number}-product-${orderItem?.productName}-name`}
                    >
                      {orderItem?.productName}
                    </span>{' '}
                    (
                    <span
                      data-testid={`order-${order?.number}-product-${orderItem?.productName}-quantity`}
                    >
                      {orderItem?.quantityOrdered}
                    </span>
                    ){(order.items?.length ?? 0) - 1 === index ? '' : ', '}
                  </span>
                ))}
              </td>

              <td data-testid={`order-${order?.number}-number`}>
                <b>{order?.number}</b>
              </td>

              <td data-testid={`order-${order?.number}-price`}>
                <FormattedMoney
                  value={order.total?.grandTotal.value}
                  currency={order.total?.grandTotal?.currency}
                />
              </td>

              <td data-testid={`order-${order?.number}-return`}>
                <ReturnInitiationButton
                  order={order}
                  onClickReturn={() => onClickReturn(order.id)}
                />
              </td>

              <td data-testid={`order-${order?.number}-url`}>
                <Link to={`/:language/account/orders/${order.number}`}>
                  <FormattedMessage {...messages.orderViewDetails} />
                </Link>
              </td>
            </tr>
          ) : null,
        )}
      </tbody>
    </OrderHistoryTableWrapper>
  );
};

export default OrderHistoryItems;

function OrderHistoryTableWrapper({ children }: { children: ReactNode }) {
  const isMobile = useMediaQuery(`(max-width: ${screenExtraSmall})`, {
    ssrMatch: true,
  });

  return (
    <Box
      sx={{
        width: '100%',
        overflowX: 'scroll',
        fontSize: rem(16),
      }}
    >
      {isMobile ? (
        <OrderHistoryTableMobile>{children}</OrderHistoryTableMobile>
      ) : (
        <OrderHistoryTable>{children}</OrderHistoryTable>
      )}
    </Box>
  );
}
